import type React from "react";
import {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
	type ReactNode,
} from "react";

export interface BreadcrumbItem {
	label: string;
	path?: string;
}

interface PageTitleContextType {
	title: string;
	breadcrumbs: BreadcrumbItem[];
	setPageTitle: (title: string, breadcrumbs?: BreadcrumbItem[]) => void;
}

const PageTitleContext = createContext<PageTitleContextType>({
	title: "",
	breadcrumbs: [],
	setPageTitle: () => {},
});

export const usePageTitle = () => {
	const context = useContext(PageTitleContext);
	if (!context) {
		throw new Error("usePageTitle must be used within a PageTitleProvider");
	}
	return context;
};

interface PageTitleProviderProps {
	children: ReactNode;
}

export const PageTitleProvider: React.FC<PageTitleProviderProps> = ({
	children,
}) => {
	const [title, setTitle] = useState("");
	const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);

	const setPageTitle = useCallback(
		(newTitle: string, newBreadcrumbs: BreadcrumbItem[] = []) => {
			setTitle(newTitle);
			setBreadcrumbs(newBreadcrumbs);
		},
		[],
	);

	const contextValue = useMemo(
		() => ({
			title,
			breadcrumbs,
			setPageTitle,
		}),
		[title, breadcrumbs, setPageTitle],
	);

	return (
		<PageTitleContext.Provider value={contextValue}>
			{children}
		</PageTitleContext.Provider>
	);
};
